/**
 * useThreadMentionSummon — on-send @-mention summon (group-threading P2b).
 *
 * Returns a STABLE `summonFromText(text, threadId)` that the send wrapper calls
 * fire-and-forget after a message is sent. It parses the message's @-mentions,
 * resolves them to workspace-member uids, and adds — via the authorized P2a
 * write path (create-then-add), exactly the path add-people proved live — the
 * ones the sender AGREED to add from the @ list's ask (2026-09-15). A mentioned
 * person not in the thread and not consented is named under the message,
 * never added: Miles's "@stephen and @joshuav …" added Joshua by accident.
 *
 * Deliberately decoupled from the send path: it NEVER blocks, delays, or alters the
 * message send, holds no props in its callback deps (everything via refs so the
 * function identity is stable), and fails closed — any error just means the person
 * wasn't summoned; the message still sent. Inert unless the mentions flag is on.
 *
 * WHAT CHANGED 2026-09-08 (chat-thread experience plan PR-1). "@justinzelaya
 * hello" summoned nobody and SAID nothing: the handle matched no member (his
 * member doc had a blank e-mail, and the handle was not a prefix of his name),
 * and fail-closed meant a silent no-op. Silence is the one outcome the sender
 * cannot act on. So the hook now:
 *   - reads the roster through the SAME normalizer every people surface uses
 *     (profile e-mail folded into a blank member e-mail; the platform's own
 *     agent identity dropped);
 *   - reports the plain `@handles` that resolved to NOBODY through
 *     `onUnresolvedMentions`, so the host can show one line — a failure notice
 *     (D9), never a guess at who was meant. Fail-closed is unchanged: nobody
 *     is ever summoned by a guess.
 */
import { useCallback, useRef } from 'react';
import { resolveMentionsToUids, resolveThreadMentionsEnabled } from '../../services/threadMentions';
import { partitionMentionedOutsiders } from '../../services/threadMentions/mentionAddConsent';
import {
  findUnresolvedMentionHandles,
  toMentionRoster,
} from '../../services/threadMentions/unresolvedMentions';
import type { ThreadMembershipSource } from '../../services/threadChoicePanel';
import { runAddParticipant } from '../../services/threadAddPeople';
import {
  addThreadParticipant,
  createThreadMembership,
} from '../../repositories/threadMembershipWriteClient';
import { getAccountMembers } from '../../services/prest0nRuntime/accountMembership';

export interface UseThreadMentionSummonArgs {
  accountId: string | null | undefined;
  selfId: string | null | undefined;
  /** Current owner-visible title, captured when a mention first registers the thread. */
  threadTitle?: string;
  /** Track B membership source — used to skip members already in the thread + refresh. */
  membershipSource?: ThreadMembershipSource;
  enabledAccountIds?: Iterable<string>;
  /**
   * Plain `@handles` in the sent text that matched NO teammate (and were not
   * Prest0n). Called at most once per send, de-duplicated, only when there is
   * at least one. The host renders the notice; this hook never decides copy.
   */
  onUnresolvedMentions?: (handles: string[]) => void;
  /**
   * CONSENT (2026-09-15, mentionAddConsent.ts): the people the sender agreed
   * to add from the @ list's ask, for `threadId`. Read once per send and
   * consumed by the host. Absent ⇒ nobody has consented ⇒ nobody is added:
   * adding a person to a thread is an explicit act, never a side effect of
   * typing a name. (Before this, every mentioned outsider was added silently.)
   */
  confirmedOutsiders?: (threadId: string) => ReadonlySet<string>;
  /**
   * People mentioned from OUTSIDE the thread who were NOT added because
   * nothing asked (no consent on record). Called at most once per send with
   * their labels; the host says so under the message. Never decides copy.
   */
  onUnconfirmedOutsiders?: (people: Array<{ uid: string; label: string }>, threadId: string) => void;
}

export interface ThreadMentionSummonController {
  enabled: boolean;
  /** Fire-and-forget: summon the message's @-mentioned members into `threadId`. */
  summonFromText: (text: string, threadId: string) => Promise<void>;
}

// The pure rules (which handles matched nobody; the one roster normalizer)
// live in services/threadMentions/unresolvedMentions and are unit-tested
// there; re-exported so existing importers keep working.
export { findUnresolvedMentionHandles, toMentionRoster };

export function useThreadMentionSummon(
  args: UseThreadMentionSummonArgs,
): ThreadMentionSummonController {
  const accountId = (args.accountId ?? '').trim();
  const selfId = (args.selfId ?? '').trim();
  const { membershipSource, enabledAccountIds } = args;

  const enabled = resolveThreadMentionsEnabled({
    accountId: accountId || null,
    enabledAccountIds,
  });

  // Everything via refs so `summonFromText` has a STABLE identity (it's captured into
  // a ref and invoked from the incident-sensitive send wrapper — no dep churn).
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const accountRef = useRef(accountId);
  accountRef.current = accountId;
  const selfRef = useRef(selfId);
  selfRef.current = selfId;
  const titleRef = useRef(args.threadTitle);
  titleRef.current = args.threadTitle;
  const sourceRef = useRef<ThreadMembershipSource | undefined>(membershipSource);
  sourceRef.current = membershipSource;
  const onUnresolvedRef = useRef(args.onUnresolvedMentions);
  onUnresolvedRef.current = args.onUnresolvedMentions;
  const confirmedRef = useRef(args.confirmedOutsiders);
  confirmedRef.current = args.confirmedOutsiders;
  const onUnconfirmedRef = useRef(args.onUnconfirmedOutsiders);
  onUnconfirmedRef.current = args.onUnconfirmedOutsiders;

  const summonFromText = useCallback(async (text: string, threadId: string): Promise<void> => {
    try {
      if (!enabledRef.current) return;
      const tid = (threadId ?? '').trim();
      const acct = accountRef.current;
      if (!tid || !acct || !text) return;
      const titleAtSend = titleRef.current;

      const roster = toMentionRoster(await getAccountMembers(acct, { fromServer: true }));
      // Say what resolved to nobody BEFORE any add — the sender is still
      // looking at the composer, and this is the moment the line helps.
      try {
        const unresolved = findUnresolvedMentionHandles(text, roster);
        if (unresolved.length > 0) onUnresolvedRef.current?.(unresolved);
      } catch { /* the notice is best-effort; the summon below is the contract */ }
      const uids = resolveMentionsToUids(text, roster).filter((u) => u && u !== selfRef.current);
      if (!uids.length) return;

      // Skip members already in the thread (best-effort; the server re-checks anyway).
      let existing = new Set<string>();
      const src = sourceRef.current;
      if (src) {
        try {
          const snaps = (await src.snapshot()) ?? [];
          const snap = snaps.find((s) => s.threadId === tid);
          if (snap) existing = new Set(snap.participantIds);
        } catch {
          /* fail-open on the skip set only */
        }
      }
      // Only people the sender AGREED to add are added (2026-09-15). The
      // rest are named under the message; the consent set is read once and
      // consumed by the host, so a mention deleted before sending adds nobody
      // (the partition intersects consent with what was actually sent).
      let confirmed: ReadonlySet<string> = new Set<string>();
      try {
        confirmed = confirmedRef.current?.(tid) ?? new Set<string>();
      } catch {
        /* no consent readable ⇒ no consent */
      }
      const { add: toAdd, heldBack } = partitionMentionedOutsiders(uids, existing, confirmed);
      if (heldBack.length) {
        try {
          const labelFor = (uid: string): string => {
            const m = roster.find((r) => r.userId === uid);
            return (m?.displayName || m?.email || uid).trim();
          };
          onUnconfirmedRef.current?.(heldBack.map((uid) => ({ uid, label: labelFor(uid) })), tid);
        } catch { /* the notice is best-effort */ }
      }
      if (!toAdd.length) return;

      // Ensure the membership doc exists, then add each consented member (authorized P2a).
      for (const uid of toAdd) {
        await runAddParticipant(
          {
            ensureThread: (t) => createThreadMembership(t, undefined, undefined, titleAtSend),
            addParticipant: (t, u) => addThreadParticipant(t, u),
          },
          tid,
          uid,
        );
      }

      // Re-hydrate membership so the panel reflects the summoned participants.
      const s = sourceRef.current as
        | (ThreadMembershipSource & { refresh?: () => Promise<void> })
        | undefined;
      if (s && typeof s.refresh === 'function') {
        try {
          await s.refresh();
        } catch {
          /* stale-until-reopen is acceptable */
        }
      }
    } catch {
      /* fire-and-forget, fail-closed: a summon failure never affects the send */
    }
  }, []);

  return { enabled, summonFromText };
}
